import { compareDesc, eachDayOfInterval, format, isSameMinute, isSaturday, isSunday, lastDayOfMonth, nextSaturday, previousSunday, startOfDay, startOfMonth } from 'date-fns/fp';
import { DateRange, deserializeDateRange, SerializedDateRange } from 'JS/types/Generic';

/**
 * A simple collection for determining whether a given date exists within itself.
 * It's sort of a simulated subset (heh) of Set to allow lookup of Date objects by value.
 * A plain set would be preferable, but not feasible given that a) Set compares by strict equality, with
 * no means of configuration and b) Dates are compared by referential equality. A date used for lookup
 * may not be an instance of the same date that was used to create the date lookup
 */
export interface DateLookup {
	/**
	 * Determines whether the lookup contains the given date
	 * @param date
	 */
	has(date: Date): boolean;

	readonly values: Date[];
}

/**
 * Creates a DateLookup given a range of dates
 * @param values {string[] | Date[]} Array of dates to be included within the lookup. Should be an array of Date objects OR an array of strings that can be parsed by the Date constructor
 */
export function createDateLookup(values: string[] | Date[]): DateLookup {
	const normalizedDates = values.map((maybeDate: string | Date) =>
		startOfDay(
			maybeDate instanceof Date
				? maybeDate
				: new Date(maybeDate)
		)
	);

	const map = new Map(normalizedDates.map(d => [d.toDateString(), d]));

	return {
		has(date: Date) {
			return map.has(date.toDateString());
		},
		get values() {
			return Array.from(map.values());
		}
	};
}

/**
 * Generates a key for time slot lookup. Should be used with the `start` date of a DateRange.
 */
const getTimeSlotMapKey = format('MM-dd-Y');

/**
 * A simple, Map-like collection for mapping Dates to time slots (DateRanges). The key will be equivalent to the day
 * of DateRange.start
 */
export interface TimeSlotLookup {
	/**
	 * Returns all time slots in the lookup that start on the same day as the given Date
	 */
	getAllForDay(date: Date): DateRange[];

	/**
	 * Returns all time slots in the lookup
	 */
	get values(): Readonly<DateRange[]>;
}

/**
 * Creates a TimeSlotLookup given an array of time slots
 * @param values An array of DateRange OR SerializedDate range
 */
export function createTimeSlotLookup(values: DateRange[] | SerializedDateRange[]): TimeSlotLookup {
	const ranges = values.map(deserializeDateRange).sort((a, b) => compareDesc(a.start, b.start));

	const map = new Map<string, DateRange[]>();

	for (const range of ranges) {
		const dayKey = getTimeSlotMapKey(range.start);
		const rangesForDay = map.get(dayKey) ?? [];
		rangesForDay.push(range);
		map.set(dayKey, rangesForDay);
	}

	return {
		getAllForDay(date: Date): DateRange[] {
			return map.get(getTimeSlotMapKey(date)) ?? [];
		},
		get values() {
			return ranges;
		}
	};
}

/**
 * Compares two DateRanges. If the start and end dates align on the same minute, they're considered equivalent.
 * @param a
 * @param b
 */
export function timeSlotsAreEquivalent(a: DateRange, b: DateRange): boolean {
	return isSameMinute(a.start, b.start) && isSameMinute(a.end, b.end);
}

/**
 * Returns a range of dates representing a full calendar month. Includes leading and trailing dates depending
 * on what day of the week and the month of the given date start/end on.
 *
 * @param date Reference date for the calendar; calendar will be generated based on the date's month and year.
 */
export function getCalendarContainingDate(date: Date): Date[] {
	const firstOfMonth = startOfMonth(date);
	const lastOfMonth = lastDayOfMonth(date);

	const start = isSunday(firstOfMonth)
		? firstOfMonth
		: previousSunday(firstOfMonth);
	const end = isSaturday(lastOfMonth)
		? lastOfMonth
		: nextSaturday(lastOfMonth);

	return eachDayOfInterval({ start, end });
}